// @ts-strict-ignore
import React, { useState } from 'react'
import { Input, Button, Radio, RadioGroup, Icon } from '../../../../atoms'
import { Stack } from '@uswitch/spark'
import { TruArrow } from '@uswitch/spark-icons/tru'
import {
  OverpaymentCalculations,
  MortgageMonthlyCostRepayment,
} from '../../../helpers/mortgages-calculations'
import loadable from '@loadable/component'
import getWindow from '../../../../utils/get-window'
import FormatCurrency from '../utils/currency-formater'
const PoweredByMojoTrustPilot = loadable(() => import('../../powered-by-mojo-trustpilot'))

interface OverpaymentCalculationsValues {
  overBalance: number
  overTermYears: number
  overTermMonths: number
  overInterest: number
  overpaymentType: OverpaymentOptions
  overPaymentOne: number
  overPaymentMonth: number
}

interface OverpaymentCalculationsResults {
  origInterest: number
  overIntDiff: number
  overTermDiff: string
  refinedInterest: number
}

interface MonthlyCostValues {
  amountBorrowed: number
  deposit: number
  interestRate: number
  mortgageTerm: number
  mortgageTermMonths: number
  repaymentType: MortgageTypes
}

interface MonthlyCostResults {
  interest: number
  interestCost: number
  monthly: number
  mortgageAmount: number
  term: number
  totalRepayment: number
  type: MortgageTypes
}

interface ResultsColumn {
  totalToRepay: number
  monthlyRepayment: number
  interestToBePaid: number
  remainingMortgageTerm: {
    years: number
    months: number
  }
}

interface CalculationResults {
  withoutOverpayments: ResultsColumn
  withOverpayments: ResultsColumn
}

enum Step {
  INITIAL = 'initial',
  REGULAR_MONTHLY = 'regularMonthly',
  LUMP_SUM = 'lumpSum',
  BOTH = 'both',
  SPECIFIED_PERIOD = 'specifiedPeriod',
  INDEFINITELY = 'indefinitely',
  RESULTS = 'results',
}

type OverpaymentOptions = 'Both' | 'One-off' | 'Recurring'

type MortgageTypes = 'interestOnly' | 'repayment'

const _mortgageType = 'mortgageType'
const _mortgageLoanAmount = 'mortgageLoanAmount'
const _years = 'years'
const _months = 'months'
const _interestRate = 'interestRate'
const _overpaymentMonthlyAmount = 'overpaymentMonthlyAmount'
const _lumpsum = 'lumpsum'

const MortgageAffordabilityCalculator = () => {
  const [step, setStep] = useState<Step>(Step.INITIAL)
  const [nextStep, setNextStep] = useState<Step>(Step.REGULAR_MONTHLY)

  const [mortgageLoanAmount, setMortgageLoanAmount] = useState<number>(100000)
  const [years, setYears] = useState<number>(10)
  const [months, setMonths] = useState<number>(0)
  const [interestRate, setInterestRate] = useState<number>(4.5)
  const [mortgageType, setMortgageType] = useState<MortgageTypes>('repayment')
  const [overpaymentsOptions, setOverpaymentsOptions] = useState<OverpaymentOptions>('Recurring')
  const [overpaymentMonthlyAmount, setOverpaymentMonthlyAmount] = useState<number>(0)
  const [lumpsum, setLumpsum] = useState<number>(0)
  const [withoutOverpaymentResults, setWithoutOverpaymentResults] = useState<ResultsColumn>({
    totalToRepay: 0,
    monthlyRepayment: 0,
    interestToBePaid: 0,
    remainingMortgageTerm: { years: 0, months: 0 },
  })
  const [withOverpaymentResults, setWithOverpaymentResults] = useState<ResultsColumn>({
    totalToRepay: 0,
    monthlyRepayment: 0,
    interestToBePaid: 0,
    remainingMortgageTerm: { years: 0, months: 0 },
  })

  const runCalculation = (): any => {
    const overpaymentCalculationValues: OverpaymentCalculationsValues = {
      overBalance: mortgageLoanAmount,
      overTermYears: years,
      overTermMonths: months,
      overInterest: interestRate,
      overpaymentType: overpaymentsOptions,
      overPaymentOne: lumpsum,
      overPaymentMonth: overpaymentMonthlyAmount,
    }

    const mortgageMonthlyCostValues: MonthlyCostValues = {
      amountBorrowed: mortgageLoanAmount,
      deposit: 0,
      interestRate,
      mortgageTerm: years,
      mortgageTermMonths: months,
      repaymentType: 'repayment',
    }

    const overpaymentsResults: OverpaymentCalculationsResults = OverpaymentCalculations(
      overpaymentCalculationValues,
    )
    const mortgageMonthlyCostResults: MonthlyCostResults =
      MortgageMonthlyCostRepayment(mortgageMonthlyCostValues)

    const termDifference = parseTermDifference(overpaymentsResults.overTermDiff)
    const refinedTerm = getRefinedTerm(years, months, termDifference.years, termDifference.months)

    return {
      withoutOverpayments: {
        totalToRepay: mortgageMonthlyCostResults.totalRepayment,
        monthlyRepayment: mortgageMonthlyCostResults.monthly,
        interestToBePaid: mortgageMonthlyCostResults.totalRepayment - mortgageLoanAmount,
        remainingMortgageTerm: { years, months },
      },
      withOverpayments: {
        totalToRepay: mortgageLoanAmount + overpaymentsResults.refinedInterest,
        monthlyRepayment: mortgageMonthlyCostResults.monthly + overpaymentMonthlyAmount,
        interestToBePaid: overpaymentsResults.refinedInterest,
        remainingMortgageTerm: { years: refinedTerm.years, months: refinedTerm.months },
      },
    }
  }

  const runCalculationInterestOnly = (): CalculationResults => {
    const defaultValues: MonthlyCostValues = {
      amountBorrowed: mortgageLoanAmount,
      deposit: 0,
      interestRate,
      mortgageTerm: years,
      mortgageTermMonths: months,
      repaymentType: 'interestOnly',
    }
    const mortgageMonthlyCostValuesOriginal: MonthlyCostValues = { ...defaultValues }
    const mortgageMonthlyCostValuesRefined: MonthlyCostValues = {
      ...defaultValues,
      amountBorrowed: mortgageLoanAmount - lumpsum,
    }

    const mortgageMonthlyCostResultsOriginal: MonthlyCostResults = MortgageMonthlyCostRepayment(
      mortgageMonthlyCostValuesOriginal,
    )
    const mortgageMonthlyCostResultsRefined: MonthlyCostResults = MortgageMonthlyCostRepayment(
      mortgageMonthlyCostValuesRefined,
    )

    return {
      withoutOverpayments: {
        totalToRepay: mortgageMonthlyCostResultsOriginal.totalRepayment,
        monthlyRepayment: mortgageMonthlyCostResultsOriginal.monthly,
        interestToBePaid: mortgageMonthlyCostResultsOriginal.interestCost,
        remainingMortgageTerm: { years, months },
      },
      withOverpayments: {
        totalToRepay: mortgageLoanAmount + mortgageMonthlyCostResultsRefined.interestCost,
        monthlyRepayment: mortgageMonthlyCostResultsRefined.monthly,
        interestToBePaid: mortgageMonthlyCostResultsRefined.interestCost,
        remainingMortgageTerm: { years, months },
      },
    }
  }

  const toNumber = (numberString: string) => parseInt(numberString, 10)

  const reset = () => {
    setStep(Step.INITIAL)
    setNextStep(Step.REGULAR_MONTHLY)
    setMortgageType('repayment')
    setOverpaymentsOptions('Recurring')
    setLumpsum(0)
    setOverpaymentMonthlyAmount(0)
  }

  const handleSubmit = () => {
    if (step === Step.INITIAL) {
      setStep(nextStep)
      setNextStep(Step.SPECIFIED_PERIOD)
    }

    if (step === Step.REGULAR_MONTHLY || step === Step.LUMP_SUM || step === Step.BOTH) {
      let results
      if (mortgageType === 'repayment') {
        results = runCalculation()
      } else {
        results = runCalculationInterestOnly()
      }
      setWithoutOverpaymentResults(results.withoutOverpayments)
      setWithOverpaymentResults(results.withOverpayments)
      setStep(Step.RESULTS)
    }

    const window: any = getWindow()
    window.scrollTo(0, 0)
  }

  const InitialCtaBlock = () => (
    <div className='MortgageOverpaymentCalculator__cta-container--primary'>
      <Button withIcon variant='primary' type='button' onClick={() => handleSubmit()}>
        Next
      </Button>
    </div>
  )
  const RegularCtaBlock = () => (
    <>
      <div className='MortgageOverpaymentCalculator__cta-container--secondary'>
        <Button variant='secondary' type='button' onClick={reset}>
          Back
        </Button>
      </div>
      <div className='MortgageOverpaymentCalculator__cta-container--primary'>
        <Button withIcon variant='primary' type='button' onClick={() => handleSubmit()}>
          Calculate
        </Button>
      </div>
    </>
  )
  const ResultsCtaBlock = () => (
    <>
      <div className='MortgageOverpaymentCalculator__cta-container--secondary'>
        <Button withIcon variant='primary' type='button' onClick={() => handleSubmit()}>
          Get free mortgage advice
        </Button>
      </div>
      <div className='MortgageOverpaymentCalculator__cta-container--primary MortgageOverpaymentCalculator__recalculate'>
        <Button variant='secondary' type='button' onClick={reset}>
          Recalculate <Icon glyph={'arrow-counter-clockwise'} size={13} />
        </Button>
      </div>
    </>
  )

  const formatTermAsString = (years: number, months: number) => {
    if (months === 0) {
      return `${years} years`
    }
    if (years === 0) {
      return `${months} months`
    }
    return `${years} years and ${months} month${months > 1 ? 's' : ''}`
  }

  const parseTermDifference = (term: string) => {
    const [originalString, years, months] = term.match(/^(\d+)[\D\s]+(\d*)[\D\s]*/) || []
    return {
      originalString,
      years: years ? parseInt(years) : 0,
      months: months ? parseInt(months) : 0,
    }
  }

  const convertYearsAndMonthsToMonths = (years: number, months: number): number => {
    return years * 12 + months
  }

  const convertMonthsToYearsAndMonths = (months: number): { months: number; years: number } => {
    return {
      years: Math.floor(months / 12),
      months: months % 12,
    }
  }

  const getRefinedTerm = (
    originalYears: number,
    originalMonths: number,
    differenceYears: number,
    differenceMonths: number,
  ): { months: number; years: number } => {
    const originalMonthsTotal = convertYearsAndMonthsToMonths(originalYears, originalMonths)
    const differenceMonthsTotal = convertYearsAndMonthsToMonths(differenceYears, differenceMonths)
    const result = originalMonthsTotal - differenceMonthsTotal
    return convertMonthsToYearsAndMonths(result)
  }

  return (
    <div className='MortgageOverpaymentCalculator'>
      <div className='MortgageOverpaymentCalculator__main-content-container'>
        <div className='MortgageOverpaymentCalculator__content-container'>
          <div className='MortgageOverpaymentCalculator__left-side'>
            <h1 className='MortgageOverpaymentCalculator__heading'>
              Mortgage overpayment calculator
            </h1>
            <p className='MortgageOverpaymentCalculator__paragraph'>
              Find out how much you could save by overpaying your mortgage.
            </p>
          </div>
          <div className='MortgageOverpaymentCalculator__inner-card'>
            <h2
              className={`MortgageOverpaymentCalculator__header ${
                step === Step.RESULTS && 'MortgageOverpaymentCalculator__header--results'
              }`}
            >
              {step === Step.RESULTS ? 'Results' : 'Calculate your overpayments'}
            </h2>
            <form
              className='MortgageOverpaymentCalculator__inner-container'
              onSubmit={handleSubmit}
            >
              {step === Step.INITIAL && (
                <>
                  <div className='MortgageOverpaymentCalculator__input-container MortgageOverpaymentCalculator__input-container--mortgage-type'>
                    <label htmlFor={_mortgageType}>Type of mortgage?</label>
                    <RadioGroup>
                      <Radio
                        name='repayment'
                        currentValue='repayment'
                        value={mortgageType}
                        onChange={() => {
                          setMortgageType('repayment')
                          setOverpaymentsOptions('Recurring')
                          setNextStep(Step.REGULAR_MONTHLY)
                        }}
                      >
                        Repayment
                      </Radio>
                      <Radio
                        name='interestOnly'
                        currentValue='interestOnly'
                        value={mortgageType}
                        onChange={() => {
                          setMortgageType('interestOnly')
                          setOverpaymentsOptions('One-off')
                          setNextStep(Step.LUMP_SUM)
                        }}
                      >
                        Interest only
                      </Radio>
                    </RadioGroup>
                  </div>
                  <div className='MortgageOverpaymentCalculator__input-container'>
                    <label htmlFor={_mortgageLoanAmount}>
                      How much do you owe on your current mortgage?
                    </label>
                    <Input
                      name={_mortgageLoanAmount}
                      prefix='£'
                      format='thousands'
                      value={mortgageLoanAmount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setMortgageLoanAmount(
                          isNaN(toNumber(e.target.value)) ? 0 : toNumber(e.target.value),
                        )
                      }
                      type='text'
                    />
                  </div>
                  <div className='MortgageOverpaymentCalculator__input-container'>
                    <label htmlFor={_years}>How long is left on your mortgage?</label>
                    <Stack direction='row'>
                      <Input
                        name={_years}
                        suffix={years === 1 ? 'year' : 'years'}
                        value={years}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setYears(isNaN(toNumber(e.target.value)) ? 0 : toNumber(e.target.value))
                        }
                        type='number'
                      />
                      <Input
                        name={_months}
                        suffix='months'
                        value={months}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setMonths(isNaN(toNumber(e.target.value)) ? 0 : toNumber(e.target.value))
                        }
                        type='number'
                      />
                    </Stack>
                  </div>
                  <div className='MortgageOverpaymentCalculator__input-container'>
                    <label htmlFor={_interestRate}>What is your current interest rate?</label>
                    <Input
                      name={_interestRate}
                      prefix='%'
                      value={interestRate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = parseFloat(e.target.value)
                        setInterestRate(isNaN(val) ? 0 : val)
                      }}
                      type='text'
                    />
                  </div>
                  {mortgageType === 'repayment' && (
                    <div className='MortgageOverpaymentCalculator__input-container'>
                      <label htmlFor='monthly-overpayments'>Overpayment options</label>
                      <RadioGroup stack={true}>
                        <Radio
                          name='monthly-overpayments'
                          value={nextStep}
                          currentValue={Step.REGULAR_MONTHLY}
                          onChange={() => {
                            setOverpaymentsOptions('Recurring')
                            setNextStep(Step.REGULAR_MONTHLY)
                          }}
                        >
                          A regular monthly overpayment
                        </Radio>
                        <Radio
                          name='monthly-overpayments'
                          value={nextStep}
                          currentValue={Step.LUMP_SUM}
                          onChange={() => {
                            setOverpaymentsOptions('One-off')
                            setNextStep(Step.LUMP_SUM)
                          }}
                        >
                          A one-off lump sum
                        </Radio>
                        <Radio
                          name='monthly-overpayments'
                          value={nextStep}
                          currentValue={Step.BOTH}
                          onChange={() => {
                            setOverpaymentsOptions('Both')
                            setNextStep(Step.BOTH)
                          }}
                        >
                          Both
                        </Radio>
                      </RadioGroup>
                    </div>
                  )}
                </>
              )}
              {(step === Step.REGULAR_MONTHLY || step === Step.BOTH) && (
                <>
                  <div className='MortgageOverpaymentCalculator__input-container'>
                    <label htmlFor={_overpaymentMonthlyAmount}>Monthly overpayment amount</label>
                    <div className='MortgageOverpaymentCalculator__subtext'>
                      Some lenders have a maximum overpayment amount before you are charged fees,
                      typically 10% of your remaining mortgage
                    </div>
                    <Input
                      name={_overpaymentMonthlyAmount}
                      prefix='£'
                      format='thousands'
                      value={overpaymentMonthlyAmount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setOverpaymentMonthlyAmount(
                          isNaN(toNumber(e.target.value)) ? 0 : toNumber(e.target.value),
                        )
                      }
                      type='text'
                    />
                  </div>
                </>
              )}
              {(step === Step.LUMP_SUM || step === Step.BOTH) && (
                <div className='MortgageOverpaymentCalculator__input-container'>
                  <label htmlFor={_lumpsum}>One-off lump sum amount</label>
                  <Input
                    name={_lumpsum}
                    prefix='£'
                    format='thousands'
                    value={lumpsum}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setLumpsum(toNumber(e.target.value))
                    }
                    type='text'
                  />
                </div>
              )}
              {step === Step.RESULTS && (
                <div className='MortgageOverpaymentCalculator__results-content'>
                  {lumpsum > 0 && overpaymentMonthlyAmount > 0 && (
                    <p>
                      If you overpay by £{overpaymentMonthlyAmount} a month and also pay a lump sum
                      of £{lumpsum}, we think we you could save the following...
                    </p>
                  )}
                  {overpaymentMonthlyAmount > 0 && lumpsum === 0 && (
                    <p>
                      If you overpay by £{overpaymentMonthlyAmount} a month, we think we you could
                      save the following...
                    </p>
                  )}
                  {lumpsum > 0 && overpaymentMonthlyAmount === 0 && (
                    <p>
                      If you pay a lump sum of £{lumpsum}, we think we you could save the
                      following...
                    </p>
                  )}
                  <div className='MortgageOverpaymentCalculator__results-content-columns'>
                    <div className='MortgageOverpaymentCalculator__results-content-column'>
                      <h2>Without overpayments</h2>
                      <div>
                        <p>Total to repay</p>
                        <p className='MortgageOverpaymentCalculator__results-content-value'>
                          £{FormatCurrency(withoutOverpaymentResults.totalToRepay.toFixed(2))}
                        </p>
                      </div>
                      <div>
                        <p>Monthly repayment</p>
                        <p className='MortgageOverpaymentCalculator__results-content-value'>
                          £{FormatCurrency(withoutOverpaymentResults.monthlyRepayment.toFixed(2))}
                        </p>
                      </div>
                      <div>
                        <p>Interest to be paid</p>
                        <p className='MortgageOverpaymentCalculator__results-content-value'>
                          £{FormatCurrency(withoutOverpaymentResults.interestToBePaid.toFixed(2))}
                        </p>
                      </div>
                      <div>
                        <p>Remaining mortgage term</p>
                        <p className='MortgageOverpaymentCalculator__results-content-value'>
                          {formatTermAsString(
                            withoutOverpaymentResults.remainingMortgageTerm.years,
                            withoutOverpaymentResults.remainingMortgageTerm.months,
                          )}
                        </p>
                      </div>
                    </div>
                    <div className='MortgageOverpaymentCalculator__results-content-column'>
                      <h2>With overpayments</h2>
                      <div>
                        <p>Total to repay</p>
                        <p className='MortgageOverpaymentCalculator__results-content-value'>
                          £{FormatCurrency(withOverpaymentResults.totalToRepay.toFixed(2))}
                          <span className='MortgageOverpaymentCalculator__results-content-difference'>
                            <TruArrow color='#36AD59' size={16} /> £
                            {FormatCurrency(
                              (
                                withoutOverpaymentResults.totalToRepay -
                                withOverpaymentResults.totalToRepay
                              ).toFixed(2),
                            )}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p>Monthly repayment</p>
                        <p className='MortgageOverpaymentCalculator__results-content-value'>
                          £{FormatCurrency(withOverpaymentResults.monthlyRepayment.toFixed(2))}
                          {withOverpaymentResults.monthlyRepayment > 0 && (
                            <span className='MortgageOverpaymentCalculator__results-content-difference MortgageOverpaymentCalculator__results-content-difference--negative'>
                              <TruArrow color='#F94549' size={16} /> £
                              {FormatCurrency(
                                (
                                  withOverpaymentResults.monthlyRepayment -
                                  withoutOverpaymentResults.monthlyRepayment
                                ).toFixed(2),
                              )}
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p>Interest to be paid</p>
                        <p className='MortgageOverpaymentCalculator__results-content-value'>
                          £{FormatCurrency(withOverpaymentResults.interestToBePaid.toFixed(2))}
                          <span className='MortgageOverpaymentCalculator__results-content-difference'>
                            <TruArrow color='#36AD59' size={16} /> £
                            {FormatCurrency(
                              (
                                withoutOverpaymentResults.interestToBePaid -
                                withOverpaymentResults.interestToBePaid
                              ).toFixed(2),
                            )}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p>Remaining mortgage term</p>
                        <p className='MortgageOverpaymentCalculator__results-content-value'>
                          {formatTermAsString(
                            withOverpaymentResults.remainingMortgageTerm.years,
                            withOverpaymentResults.remainingMortgageTerm.months,
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className='MortgageOverpaymentCalculator__cta-container'>
                {step === Step.INITIAL && <InitialCtaBlock />}
                {(step === Step.REGULAR_MONTHLY ||
                  step === Step.LUMP_SUM ||
                  step === Step.BOTH ||
                  step === Step.SPECIFIED_PERIOD ||
                  step === Step.INDEFINITELY) && <RegularCtaBlock />}
                {step === Step.RESULTS && <ResultsCtaBlock />}
              </div>
              <div className='MortgageOverpaymentCalculator__powered-by-mojo-logo'>
                <PoweredByMojoTrustPilot
                  link={true}
                  businessId='573992ca0000ff00058cec06'
                  templateId='5419b732fbfb950b10de65e5'
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MortgageAffordabilityCalculator
